import { useState } from "react";
import { TeamCapabilityGaps } from "@/components/persons/TeamCapabilityGaps";
import { RoleCapabilityMatrix } from "@/components/roles/RoleCapabilityMatrix";
import { RoleCategoryMatrix } from "@/components/roles/RoleCategoryMatrix";
import { useSetBreadcrumbs } from "@/components/shared/breadcrumbs";
import { PageHeader } from "@/components/shared/page-header";
import { TabBar, type TabItem } from "@/components/shared/tab-bar";

/**
 * Capability: what roles ask for, and where people fall short of it —
 * EPIC-023 (WI-042, WI-308) plus the role × capability matrix.
 *
 * Two tabs rather than two pages. They answer consecutive questions about the
 * same thing — "what does this role require" and "who is short of it" — and
 * having them in separate places in the nav meant leaving one to check the
 * other.
 */

type TabKey = "gaps" | "matrix" | "categories";

const TABS: TabItem[] = [
  { key: "gaps", label: "Gaps" },
  { key: "matrix", label: "Requirements matrix" },
  { key: "categories", label: "Category overlap" },
];

const DESCRIPTIONS: Record<TabKey, string> = {
  gaps: "Where a team falls short of what their roles require, from closed review rounds. Only capabilities someone has actually been rated on are counted.",
  matrix:
    "Which roles require which skills and competencies. Roles are rows; capabilities are columns. A filled cell means the role requires that capability.",
  categories:
    "The same requirements a level up: which categories each role draws on, so two roles sharing one can be seen to overlap. A role whose skills and competencies are not yet filed under a category says so rather than looking like it has nothing in common.",
};

export function CapabilityGapsPage() {
  // Both crumbs distinct: two identical ones render as "Capability ›
  // Capability" and log a duplicate-key warning on every render — the same
  // bug the Certifications page had.
  useSetBreadcrumbs([{ label: "Capability" }, { label: "Capabilities" }]);
  const [tab, setTab] = useState<TabKey>("gaps");

  return (
    <div className="space-y-6" data-wide-page>
      <PageHeader eyebrow="Capability" title="Capabilities" description={DESCRIPTIONS[tab]} />
      <TabBar tabs={TABS} value={tab} onChange={(key) => setTab(key as TabKey)} />
      {tab === "gaps" && <TeamCapabilityGaps />}
      {tab === "matrix" && <RoleCapabilityMatrix />}
      {tab === "categories" && <RoleCategoryMatrix />}
    </div>
  );
}
